import {useFocusManagement} from '@aboutyou/a11y';
import {Fade, Slide} from '@aboutyou/transitions';
import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {tabletAndUpLinaria} from '../../../constants/customMedia';
import {Z_INDEXES} from '../../../constants/layers';
import {SPACING} from '../../../constants/sizing';
import {useKeyPress} from '../../../hooks/useKeyPress/useKeyPress';
import {useLockBodyScroll} from '../../../hooks/useLockBodyScroll/useLockBodyScroll';
import {DRAWER_PORTAL} from '../../../hooks/usePortalElement/portalIds';
import {BlurredOverlay} from '../BlurredOverlay/BlurredOverlay';
import {ContentPopperBaseProps} from '../ContentPopper/ContentPopper';
import {FooterWrapper} from '../ContentPopper/ContentPopperButtons/ContentPopperButtons';
import {ContentPopperHeader} from '../ContentPopper/ContentPopperHeader';
import {
  CONTENT_POPPER_ANIMATION_DURATION_MOUNT,
  CONTENT_POPPER_ANIMATION_DURATION_UNMOUNT,
} from '../ContentPopper/utils/contentPopperAnimationDurations';
import {RenderInPortal} from '../RenderInPortal/RenderInPortal';

export interface DrawerProps
  extends Omit<ContentPopperBaseProps, 'hideCloseButton'> {
  slideDirection?: 'left' | 'right';
  OverlayComponent?: React.ComponentType<
    React.ComponentProps<typeof BlurredOverlay>
  >;
  setTransparentHeaderBackground?: boolean;
  preventScrollLock?: boolean;
  hideBackdrop?: boolean;
}

export const Drawer = React.forwardRef<HTMLElement, DrawerProps>(
  function DrawerComponent(
    {
      'data-testid': dataTestId,
      headerContent,
      bodyContent,
      onClose,
      onGoBack,
      open,
      slideDirection = 'right',
      renderInPortal,
      OverlayComponent = BlurredOverlay,
      preventAnimation,
      preventScrollLock,
      popoverPriority,
      hideBackdrop,
      footerContent,
      ...rest
    },
    ref,
  ) {
    const {isTopMostPopover, focusManagementRefCallback} = useFocusManagement({
      isOpen: open,
      priority: popoverPriority,
    });
    useLockBodyScroll({lockScroll: preventScrollLock ? false : open});
    useKeyPress({
      key: 'Escape',
      onKeyDown: onClose,
      disabled: !open || !isTopMostPopover,
    });

    const drawerContent = (
      <>
        {!hideBackdrop && (
          <Fade
            mounted={open}
            duration={{
              mount: CONTENT_POPPER_ANIMATION_DURATION_MOUNT,
              unmount: CONTENT_POPPER_ANIMATION_DURATION_UNMOUNT,
            }}
          >
            <OverlayComponent
              $zIndex={Z_INDEXES.OFF_CANVAS}
              data-testid="drawerBackdrop"
              onClick={e => {
                e.stopPropagation();
                onClose();
              }}
            />
          </Fade>
        )}
        <Slide
          mounted={open}
          direction={slideDirection}
          duration={{
            mount: CONTENT_POPPER_ANIMATION_DURATION_MOUNT,
            unmount: CONTENT_POPPER_ANIMATION_DURATION_UNMOUNT + 50,
          }}
          preventAnimationOnMount={preventAnimation}
        >
          <FixedContainer
            aria-modal="true"
            role="dialog"
            tabIndex={-1}
            data-direction={slideDirection}
            visible={open}
            {...rest}
          >
            <DrawerContainer
              data-testid={dataTestId}
              ref={focusManagementRefCallback(ref)}
              tabIndex={0}
            >
              {headerContent && (
                <DrawerHeader
                  data-testid={dataTestId}
                  onClose={onClose}
                  onGoBack={onGoBack}
                >
                  {headerContent}
                </DrawerHeader>
              )}

              {Array.isArray(bodyContent) ? (
                bodyContent
                  .filter(section => !!section)
                  .map((bodySection, key) => {
                    return (
                      <React.Fragment key={key}>{bodySection}</React.Fragment>
                    );
                  })
              ) : (
                <DrawerBody
                  $enableTopDivider={Boolean(headerContent)}
                  $enableBottomDivider={Boolean(footerContent)}
                >
                  {bodyContent}
                </DrawerBody>
              )}
              {footerContent && <FooterWrapper>{footerContent}</FooterWrapper>}
            </DrawerContainer>
          </FixedContainer>
        </Slide>
      </>
    );

    if (renderInPortal) {
      return (
        <RenderInPortal portalId={DRAWER_PORTAL}>
          {drawerContent}
        </RenderInPortal>
      );
    }

    return <>{drawerContent}</>;
  },
);

export const DrawerHeader = styled(ContentPopperHeader)`
  background-color: ${COLORS['Fill.White']};
  padding: ${SPACING['Y.Lg']} ${SPACING['X.Lg']};

  > div {
    justify-content: flex-start;
  }
`;

export const DrawerContainer = styled.section`
  height: 100%;
  width: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;

  ${tabletAndUpLinaria('width: 400px;')};
`;

export const DrawerBody = styled.section<{
  $enableTopDivider?: boolean;
  $enableBottomDivider?: boolean;
}>`
  background-color: ${COLORS['Fill.White']};
  border-top: ${props =>
    props.$enableTopDivider
      ? `1px solid  ${COLORS['Outline.Grey']}`
      : 'initial'};
  border-bottom: ${props =>
    props.$enableBottomDivider
      ? `1px solid ${COLORS['Outline.Grey']}`
      : 'initial'};

  flex: 1 1 auto;
  padding: ${SPACING['Y.Lg']} ${SPACING['X.Lg']};
  overflow: auto;
`;

const FixedContainer = styled.div<{
  visible: boolean;
}>`
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: ${({visible}) => (visible ? 'all' : 'none')};
  touch-action: none;
  overflow: clip;
  z-index: ${Z_INDEXES.DRAWER};
  left: 0;

  ${tabletAndUpLinaria(`
    max-width: 400px;
    border-radius: 0 16px 16px 0;
  `)}

  &[data-direction='left'] {
    right: 0;
    left: unset;

    ${tabletAndUpLinaria(`
      border-radius: 16px 0 0 16px;
    `)}
  }

  ${tabletAndUpLinaria(`
    border-radius: 0 16px 16px 0;
  `)}
`;
